import "../scss/consent-overlay.scss";
import { eventBus, GlobalEvents } from "@allwyn/utils/global-events";
import { WECOMA_CONSTANTS } from "@allwyn/utils/wecoma/wecoma-constants";
import { buildConsentOverlayMarkup, CONSENT_OVERLAY_CLASSES, resolveOverlayText } from "./consent-overlay-template";
import { ConsentOverlayConfig } from "./consent-overlay-types";
import { useWecomaDatalayer } from "../analytics/use-wecoma-datalayer";
import { useWecomaIntegration } from "../utils/use-wecoma-integration";

const DEFAULT_MIN_HEIGHT = 280;

function resolveTarget(target: string | HTMLElement): HTMLElement {
    const element = target instanceof HTMLElement ? target : document.querySelector<HTMLElement>(target);

    if (!element) {
        throw new Error(
            `[consent-overlay] Target element not found: ${target instanceof HTMLElement ? "<element>" : target}`
        );
    }

    return element;
}

function buildPreferenceCenterUrl(override?: string): string {
    if (override) {
        return override;
    }

    const url = new URL(appSettings["Integrations:Wecoma:Domain"]);
    url.searchParams.set("key", WECOMA_CONSTANTS.WecomaCode);
    url.searchParams.set("locale", "cs-CZ");
    url.searchParams.set("authKeys", WECOMA_CONSTANTS.WecomaAuthKey);
    url.searchParams.set("source", "thirdpartycontent");
    return url.toString();
}

function removeOverlay(host: HTMLElement): void {
    host.style.removeProperty("min-height");
    host.querySelector(`.${CONSENT_OVERLAY_CLASSES.block}`)?.remove();
}

function attachOverlayBehaviour(host: HTMLElement, overlay: HTMLElement): void {
    const { consentToThirdParty, thirdPartyConsented } = useWecomaIntegration();
    const { pushOverlayRedirectToPreferenceCenter } = useWecomaDatalayer();

    const listeners = new AbortController();
    const { signal } = listeners;

    overlay
        .querySelector(`.${CONSENT_OVERLAY_CLASSES.button}`)
        ?.addEventListener("click", () => consentToThirdParty(), { signal });

    overlay
        .querySelector(`.${CONSENT_OVERLAY_CLASSES.preferenceCenterLink}`)
        ?.addEventListener("click", () => pushOverlayRedirectToPreferenceCenter("text"), { signal });

    overlay
        .querySelector(`.${CONSENT_OVERLAY_CLASSES.link}`)
        ?.addEventListener("click", () => pushOverlayRedirectToPreferenceCenter("button"), { signal });

    const onConsentsChanged = async () => {
        if (await thirdPartyConsented()) {
            listeners.abort();
            eventBus.off(GlobalEvents.ConsentsChanged, onConsentsChanged);
            removeOverlay(host);
        }
    };

    eventBus.on(GlobalEvents.ConsentsChanged, onConsentsChanged);
}

/**
 * Conditionally mounts a consent overlay based on the user's consent status for third-party content (Wecoma)
 *
 * @param config
 * @returns A promise that resolves to `true` if the overlay was mounted, or `false` if the user has already consented and the overlay was not needed
 */
export async function mountConsentOverlay(config: ConsentOverlayConfig): Promise<boolean> {
    const host = resolveTarget(config.target);

    const { thirdPartyConsented } = useWecomaIntegration();
    if (await thirdPartyConsented()) {
        removeOverlay(host);
        return false;
    }

    useWecomaDatalayer().pushThirdParty();

    // Remove any existing overlay right before inserting so concurrent mount calls for the same host can't both insert one
    removeOverlay(host);
    host.style.minHeight = `${config.minHeight ?? DEFAULT_MIN_HEIGHT}px`;
    host.insertAdjacentHTML(
        "beforeend",
        buildConsentOverlayMarkup({
            text: resolveOverlayText(config.textConfig),
            backgroundImage: config.backgroundImage ?? "",
            backgroundImageSet: config.backgroundImageSet,
            preferenceCenterUrl: buildPreferenceCenterUrl(config.preferenceCenterUrl),
            externalContentUrl: config.externalContentUrl,
        })
    );

    const overlay = host.querySelector<HTMLElement>(`.${CONSENT_OVERLAY_CLASSES.block}`);
    if (!overlay) {
        return false;
    }

    attachOverlayBehaviour(host, overlay);

    return true;
}
